import { create } from "zustand";
import type { PostWithRelationsT } from "@/app/types/posts";

interface PostsFilterState {
  // State
  posts: PostWithRelationsT[];
  filteredPosts: PostWithRelationsT[];
  selectedCategory: string;
  searchQuery: string;
  isLoading: boolean;
  isInitialized: boolean;
  currentPage: number;
  pageSize: number;

  // Actions
  setPosts: (posts: PostWithRelationsT[]) => void;
  setSelectedCategory: (category: string) => void;
  setSearchQuery: (query: string) => void;
  clearFilters: () => void;
  filterPosts: () => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export const usePostsFilterStore = create<PostsFilterState>((set, get) => ({
  // Initial state
  posts: [],
  filteredPosts: [],
  selectedCategory: "all",
  searchQuery: "",
  isLoading: false,
  isInitialized: false,
  currentPage: 1,
  pageSize: 12,

  // Actions
  setPosts: (posts) => {
    set({ posts, filteredPosts: posts, isInitialized: true, currentPage: 1 });
  },

  setSelectedCategory: (category) => {
    set({ selectedCategory: category });
    get().filterPosts();
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    get().filterPosts();
  },

  clearFilters: () => {
    set({
      selectedCategory: "all",
      searchQuery: "",
      filteredPosts: get().posts,
      currentPage: 1,
    });
  },

  filterPosts: () => {
    const { posts, selectedCategory, searchQuery } = get();
    let filtered = [...posts];

    // Filter nach Kategorie
    if (selectedCategory && selectedCategory !== "all") {
      filtered = filtered.filter((post) =>
        post.categories.some((category) => category.key === selectedCategory)
      );
    }

    // Filter nach Suchquery
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((post) => {
        const titleMatch = post.title.toLowerCase().includes(query);
        const subtitleMatch =
          post.sub_title?.toLowerCase().includes(query) || false;
        const categoryMatch = post.categories.some((category) =>
          category.label.toLowerCase().includes(query)
        );

        return titleMatch || subtitleMatch || categoryMatch;
      });
    }

    // Reset to page 1 when filters change
    set({ filteredPosts: filtered, currentPage: 1 });
  },

  setCurrentPage: (page) => {
    const safePage = Number.isFinite(page) && page > 0 ? page : 1;
    set({ currentPage: safePage });
  },

  setPageSize: (size) => {
    const safeSize = Number.isFinite(size) && size > 0 ? size : 12;
    set({ pageSize: safeSize, currentPage: 1 });
  },
}));
